import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API } from "@/App";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { 
  Search, Building2, Package, Clock, CheckCircle2, 
  ArrowUpDown, ArrowUpRight, Users, RefreshCw, Loader2,
  Mail, Phone, MapPin, X, Eye, ChevronRight
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

// Animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.05 }
  }
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 }
};

export default function ClientsPage() {
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortField, setSortField] = useState("name");
  const [sortDir, setSortDir] = useState("asc");
  const [selectedClient, setSelectedClient] = useState(null);
  const [showClientDetail, setShowClientDetail] = useState(false);

  const fetchClients = useCallback(async () => {
    try {
      // Fetch client requirements from Zoho first
      const zohoClientsResponse = await axios.get(`${API}/zoho/client-requirements`);
      const zohoClients = zohoClientsResponse.data?.clients || [];
      
      // Create a map of client details from Zoho (by both client name and company name)
      const zohoClientMap = {};
      zohoClients.forEach(client => {
        const clientKey = (client.clientName || "").toLowerCase().trim();
        const companyKey = (client.companyName || "").toLowerCase().trim();
        
        const clientInfo = {
          clientName: client.clientName || client.companyName || "",
          companyName: client.companyName || "",
          email: client.email || "",
          phoneNumber: client.phoneNumber || "",
          location: client.location || ""
        };
        
        // Map by both client name and company name for flexible matching
        if (clientKey) {
          zohoClientMap[clientKey] = clientInfo;
        }
        if (companyKey && companyKey !== clientKey) {
          zohoClientMap[companyKey] = clientInfo;
        }
      });
      
      // Fetch all product folders
      const response = await axios.get(`${API}/product-folders`);
      if (response.data.success) {
        const folders = response.data.folders || [];
        
        // Group by client name (case-insensitive) - use lowercase key for grouping
        const clientMap = {};
        folders.forEach(folder => {
          const originalClientName = folder.clientName || "Unknown Client";
          // Use lowercase key for case-insensitive grouping
          const clientKey = originalClientName.toLowerCase().trim();
          
          // Get Zoho details for this client (try exact match first, then partial)
          let zohoDetails = zohoClientMap[clientKey] || {};
          
          // If not found, try to find a partial match
          if (!zohoDetails.email && !zohoDetails.companyName) {
            for (const [key, value] of Object.entries(zohoClientMap)) {
              if (clientKey.includes(key) || key.includes(clientKey)) {
                zohoDetails = value;
                break;
              }
            }
          }
          
          if (!clientMap[clientKey]) {
            clientMap[clientKey] = {
              name: originalClientName.trim(),
              company: zohoDetails.companyName || originalClientName.trim(),
              companyName: zohoDetails.companyName || "",
              email: zohoDetails.email || "",
              phoneNumber: zohoDetails.phoneNumber || "",
              location: zohoDetails.location || "",
              totalJobs: 0,
              activeJobs: 0,
              completedJobs: 0,
              products: []
            };
          }
          clientMap[clientKey].totalJobs++;
          clientMap[clientKey].products.push(folder);
          
          // Count by status
          if (folder.testStatus === 'Complete' || folder.testStatus === 'Finish') {
            clientMap[clientKey].completedJobs++;
          } else if (folder.testStatus && folder.testStatus !== 'Not Started') {
            clientMap[clientKey].activeJobs++;
          }
        });
        
        setClients(Object.values(clientMap));
      }
    } catch (error) {
      console.error("Failed to fetch clients:", error);
      toast.error("Failed to fetch clients");
    } finally {
      setLoading(false);
    }
  }, []);

  // Sync clients from Zoho - re-syncs all R&D records and updates local database
  const handleSyncFromZoho = async () => {
    setSyncing(true);
    try {
      toast.loading("Syncing clients from Zoho...", { id: "sync-clients" });
      
      // First sync R&D records from Zoho to update local database
      const syncResponse = await axios.post(`${API}/sync-rd-from-zoho`);
      
      if (syncResponse.data.success) {
        toast.success(`✅ Synced ${syncResponse.data.synced || 0} records from Zoho`, { 
          id: "sync-clients",
          duration: 4000 
        });
        
        // Refresh the client list after sync
        setLoading(true);
        await fetchClients();
      } else {
        toast.error(`Sync failed: ${syncResponse.data.error || 'Unknown error'}`, { 
          id: "sync-clients" 
        });
      }
    } catch (error) {
      console.error("Failed to sync from Zoho:", error);
      toast.error(`Sync failed: ${error.response?.data?.detail || error.message}`, { 
        id: "sync-clients" 
      });
    } finally {
      setSyncing(false);
    }
  };

  useEffect(() => {
    fetchClients();
  }, [fetchClients]);

  // Filter and sort clients
  const filteredClients = clients
    .filter(client => 
      client.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      client.company?.toLowerCase().includes(searchTerm.toLowerCase())
    )
    .sort((a, b) => {
      const aVal = a[sortField] || 0;
      const bVal = b[sortField] || 0;
      if (sortDir === "asc") return aVal > bVal ? 1 : -1;
      return aVal < bVal ? 1 : -1;
    });

  const toggleSort = (field) => {
    if (sortField === field) {
      setSortDir(sortDir === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDir("asc");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-pulse text-vcc-teal font-poppins">Loading clients...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen p-8" data-testid="clients-page">
      {/* Header */}
      <motion.div 
        className="flex items-center justify-between mb-8"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <div>
          <h1 className="text-3xl font-poppins font-bold text-slate-800">Client List</h1>
          <p className="text-slate-500 mt-1 font-poppins">View all clients and their product status</p>
        </div>
        
        <div className="flex items-center gap-4">
          <div className="px-4 py-2 bg-vcc-teal/10 rounded-xl">
            <span className="text-vcc-teal font-poppins font-semibold">{clients.length} Clients</span>
          </div>
        </div>
      </motion.div>

      {/* Search & Filters */}
      <motion.div 
        className="flex flex-wrap gap-4 mb-6"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
      >
        <div className="relative flex-1 min-w-[300px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            placeholder="Search clients..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 bg-white/80 border-vcc-pink-100 focus:border-vcc-teal font-poppins"
            data-testid="search-clients"
          />
        </div>
        
        <div className="flex gap-2">
          <button
            onClick={() => toggleSort("name")}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg border transition-all font-poppins ${
              sortField === "name" 
                ? "bg-vcc-teal text-white border-vcc-teal" 
                : "bg-white/80 text-slate-600 border-vcc-pink-100 hover:border-vcc-teal"
            }`}
          >
            <ArrowUpDown size={16} /> Name
          </button>
          <button
            onClick={() => toggleSort("totalJobs")}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg border transition-all font-poppins ${
              sortField === "totalJobs" 
                ? "bg-vcc-teal text-white border-vcc-teal" 
                : "bg-white/80 text-slate-600 border-vcc-pink-100 hover:border-vcc-teal"
            }`}
          >
            <ArrowUpDown size={16} /> Total Jobs
          </button>
        </div>
      </motion.div>

      {/* Client Cards Grid */}
      <motion.div 
        className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        {filteredClients.length === 0 ? (
          <motion.div 
            variants={itemVariants}
            className="col-span-full py-16 text-center"
          >
            <Users className="w-16 h-16 mx-auto mb-4 text-slate-300" />
            <p className="text-slate-500 font-poppins">No clients found</p>
          </motion.div>
        ) : (
          filteredClients.map((client, index) => (
            <motion.div
              key={client.name}
              variants={itemVariants}
              whileHover={{ y: -5, transition: { duration: 0.2 } }}
              className="backdrop-blur-xl bg-white/90 border border-vcc-pink-100 rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all group"
              data-testid={`client-card-${index}`}
            >
              {/* Client Header */}
              <div className="flex items-start justify-between mb-6">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-vcc-teal to-vcc-coral flex items-center justify-center text-white font-poppins font-bold text-lg">
                    {client.name?.charAt(0) || 'C'}
                  </div>
                  <div>
                    <h3 className="font-poppins font-semibold text-slate-800 text-lg">{client.name}</h3>
                    {client.companyName && client.companyName !== client.name && (
                      <p className="text-sm text-vcc-teal font-poppins font-medium flex items-center gap-1">
                        <Building2 size={12} />
                        {client.companyName}
                      </p>
                    )}
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setSelectedClient(client);
                    setShowClientDetail(true);
                  }}
                  className="text-slate-400 hover:text-vcc-teal hover:bg-vcc-teal/10"
                  title="View Details"
                >
                  <Eye size={18} />
                </Button>
              </div>

              {/* Client Contact Info */}
              {(client.email || client.phoneNumber || client.location) && (
                <div className="mb-4 space-y-1.5 border-b border-slate-100 pb-4">
                  {client.email && (
                    <p className="text-xs text-slate-500 font-poppins flex items-center gap-2">
                      <Mail size={12} className="text-vcc-teal" />
                      {client.email}
                    </p>
                  )}
                  {client.phoneNumber && (
                    <p className="text-xs text-slate-500 font-poppins flex items-center gap-2">
                      <Phone size={12} className="text-vcc-teal" />
                      {client.phoneNumber}
                    </p>
                  )}
                  {client.location && (
                    <p className="text-xs text-slate-500 font-poppins flex items-center gap-2">
                      <MapPin size={12} className="text-vcc-teal" />
                      {client.location}
                    </p>
                  )}
                </div>
              )}

              {/* Stats */}
              <div className="grid grid-cols-3 gap-3">
                <div className="bg-vcc-cream-50 rounded-xl p-3 text-center">
                  <div className="flex items-center justify-center gap-1 text-vcc-teal mb-1">
                    <Package size={14} />
                  </div>
                  <p className="text-2xl font-poppins font-bold text-slate-800">{client.totalJobs}</p>
                  <p className="text-xs text-slate-500 font-poppins">Total</p>
                </div>
                
                <div className="bg-blue-50 rounded-xl p-3 text-center">
                  <div className="flex items-center justify-center gap-1 text-blue-500 mb-1">
                    <Clock size={14} />
                  </div>
                  <p className="text-2xl font-poppins font-bold text-slate-800">{client.activeJobs}</p>
                  <p className="text-xs text-slate-500 font-poppins">Active</p>
                </div>
                
                <div className="bg-emerald-50 rounded-xl p-3 text-center">
                  <div className="flex items-center justify-center gap-1 text-emerald-500 mb-1">
                    <CheckCircle2 size={14} />
                  </div>
                  <p className="text-2xl font-poppins font-bold text-slate-800">{client.completedJobs}</p>
                  <p className="text-xs text-slate-500 font-poppins">Done</p>
                </div>
              </div>

              {/* Progress Bar */}
              <div className="mt-4">
                <div className="flex items-center justify-between text-xs text-slate-500 mb-1 font-poppins">
                  <span>Completion</span>
                  <span>{client.totalJobs > 0 ? Math.round((client.completedJobs / client.totalJobs) * 100) : 0}%</span>
                </div>
                <div className="h-2 bg-vcc-cream-100 rounded-full overflow-hidden">
                  <motion.div 
                    className="h-full bg-gradient-to-r from-vcc-teal to-emerald-400"
                    initial={{ width: 0 }}
                    animate={{ width: `${client.totalJobs > 0 ? (client.completedJobs / client.totalJobs) * 100 : 0}%` }}
                    transition={{ duration: 1, delay: index * 0.1 }}
                  />
                </div>
              </div>

              {/* View Products Button */}
              <Button
                onClick={() => navigate(`/clients/${encodeURIComponent(client.name)}`)}
                className="w-full mt-4 bg-vcc-teal hover:bg-vcc-teal/90 text-white font-poppins"
              >
                View Products
                <ChevronRight size={16} className="ml-1" />
              </Button>
            </motion.div>
          ))
        )}
      </motion.div>

      {/* Client Detail Dialog */}
      <Dialog open={showClientDetail} onOpenChange={setShowClientDetail}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3 font-poppins">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-vcc-teal to-vcc-coral flex items-center justify-center text-white font-bold">
                {selectedClient?.name?.charAt(0) || 'C'}
              </div>
              <div>
                <span className="text-slate-800">{selectedClient?.name}</span>
                {selectedClient?.companyName && selectedClient?.companyName !== selectedClient?.name && (
                  <p className="text-sm font-normal text-vcc-teal">{selectedClient?.companyName}</p>
                )}
              </div>
            </DialogTitle>
          </DialogHeader>
          
          {selectedClient && (
            <div className="space-y-6 pt-4">
              {/* Contact Information */}
              <div className="space-y-3">
                <h4 className="text-sm font-semibold text-slate-700 font-poppins flex items-center gap-2">
                  <Building2 size={16} className="text-vcc-teal" />
                  Contact Information
                </h4>
                <div className="bg-slate-50 rounded-xl p-4 space-y-3">
                  {selectedClient.companyName && (
                    <div className="flex items-center gap-3">
                      <Building2 size={16} className="text-slate-400" />
                      <div>
                        <p className="text-xs text-slate-400 font-poppins">Company</p>
                        <p className="text-sm font-medium text-slate-700 font-poppins">{selectedClient.companyName}</p>
                      </div>
                    </div>
                  )}
                  {selectedClient.email && (
                    <div className="flex items-center gap-3">
                      <Mail size={16} className="text-slate-400" />
                      <div>
                        <p className="text-xs text-slate-400 font-poppins">Email</p>
                        <a href={`mailto:${selectedClient.email}`} className="text-sm font-medium text-vcc-teal hover:underline font-poppins">
                          {selectedClient.email}
                        </a>
                      </div>
                    </div>
                  )}
                  {selectedClient.phoneNumber && (
                    <div className="flex items-center gap-3">
                      <Phone size={16} className="text-slate-400" />
                      <div>
                        <p className="text-xs text-slate-400 font-poppins">Phone</p>
                        <a href={`tel:${selectedClient.phoneNumber}`} className="text-sm font-medium text-vcc-teal hover:underline font-poppins">
                          {selectedClient.phoneNumber}
                        </a>
                      </div>
                    </div>
                  )}
                  {selectedClient.location && (
                    <div className="flex items-center gap-3">
                      <MapPin size={16} className="text-slate-400" />
                      <div>
                        <p className="text-xs text-slate-400 font-poppins">Location</p>
                        <p className="text-sm font-medium text-slate-700 font-poppins">{selectedClient.location}</p>
                      </div>
                    </div>
                  )}
                  {!selectedClient.companyName && !selectedClient.email && !selectedClient.phoneNumber && !selectedClient.location && (
                    <p className="text-sm text-slate-400 font-poppins italic">No contact details available</p>
                  )}
                </div>
              </div>

              {/* Job Statistics */}
              <div className="space-y-3">
                <h4 className="text-sm font-semibold text-slate-700 font-poppins flex items-center gap-2">
                  <Package size={16} className="text-vcc-teal" />
                  Job Statistics
                </h4>
                <div className="grid grid-cols-3 gap-3">
                  <div className="bg-vcc-cream-50 rounded-xl p-4 text-center">
                    <p className="text-3xl font-poppins font-bold text-slate-800">{selectedClient.totalJobs}</p>
                    <p className="text-xs text-slate-500 font-poppins">Total Jobs</p>
                  </div>
                  <div className="bg-blue-50 rounded-xl p-4 text-center">
                    <p className="text-3xl font-poppins font-bold text-blue-600">{selectedClient.activeJobs}</p>
                    <p className="text-xs text-slate-500 font-poppins">Active</p>
                  </div>
                  <div className="bg-emerald-50 rounded-xl p-4 text-center">
                    <p className="text-3xl font-poppins font-bold text-emerald-600">{selectedClient.completedJobs}</p>
                    <p className="text-xs text-slate-500 font-poppins">Completed</p>
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-3 pt-2">
                <Button
                  variant="outline"
                  onClick={() => setShowClientDetail(false)}
                  className="flex-1 font-poppins"
                >
                  Close
                </Button>
                <Button
                  onClick={() => {
                    setShowClientDetail(false);
                    navigate(`/clients/${encodeURIComponent(selectedClient.name)}`);
                  }}
                  className="flex-1 bg-vcc-teal hover:bg-vcc-teal/90 text-white font-poppins"
                >
                  View Products
                  <ChevronRight size={16} className="ml-1" />
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
